import React, { useEffect, useState, useRef } from 'react';
import dayjs from 'dayjs';
import {
    Box, Button, Typography, Stack, Divider, useTheme, Fade, Zoom, Container, TextField, InputAdornment, Breadcrumbs, Link,
    IconButton, Tooltip, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody, TableFooter, styled,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import axiosInstance from '../../lib/axios';
import { toast, ToastContainer } from 'react-toastify';
import { useReactToPrint } from 'react-to-print';
import { useSelector } from 'react-redux';
import { selectGlobalProperty } from '../../redux/propertySlice';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { usePermissions } from '../../GlobalFunction/userPermissions';
import BillingDetailsModal from '../Ragistration/BConfirmation/ACCGenerateDetailsModal';
import ExcelJS from 'exceljs';

const MonthWiseBill = () => {
    const theme = useTheme();
    const [fromDate, setFromDate] = useState(dayjs().subtract(1, 'month'));
    const [toDate, setToDate] = useState(dayjs());
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [filteredRecords, setFilteredRecords] = useState([]);
    const [searchText, setSearchText] = useState('');
    const contentRef = useRef();
    const globalProperty = useSelector(selectGlobalProperty);
    const permissions = usePermissions("Month Wise Bill");
    const { ViewRec = "0" } = permissions || {};
    const [openModal, setOpenModal] = useState(false);
    const [planDataState, setPlanDataState] = useState(null);
    const [billingDetails, setBillingDetails] = useState(null);
    const [payAdvice, setPayAdvice] = useState(null);
    const [payGenerate, setPayGenerate] = useState(null);
    const [summaryReport, setSummaryReport] = useState(null)
    const [receiptSummary, setReceiptSummary] = useState(null);
    const [pendingDetails, setPendingDetails] = useState(null);

    const navigate = useNavigate();
    const handlePrint = useReactToPrint({
        contentRef,
        documentTitle: `Month Wise Bill`,
        pageStyle: `
        @page { 
            size: A4 portrait; 
            margin: 1cm;
            @top-center { content: "Month Wise Bill Report"; font-weight: bold; font-size: 24px; color: #635bff }
            @bottom-center { content: "Page " counter(page) " of " counter(pages); font-size: 10px; }
        }
        @media print {
            body { 
                font-family: Arial, sans-serif; 
                margin: 0;
                -webkit-print-color-adjust: exact; 
                print-color-adjust: exact; 
            }
            .no-print { display: none !important; }
            .print-header { 
                text-align: center; 
                margin-bottom: 16px; 
                page-break-after: avoid;
            }
            .print-title { 
                font-size: 18px; 
                font-weight: bold; 
                margin: 4px 0; 
                color: #635bff;
            }
            .print-subtitle { 
                font-size: 12px; 
                color: #555; 
                margin: 3px 0;
            }
            table { 
                width: 100%; 
                border-collapse: collapse; 
                font-size: 11px; 
                page-break-inside: avoid;
            }
            th { 
                background: #635bff !important; 
                color: white !important; 
                padding: 3px 6px; 
                font-weight: bold;
                text-align: left;
            }
            td { 
                padding: 5px 8px; 
                border-bottom: 1px solid #eee;
            }
            tr:nth-child(even) td { 
                background-color: #f9f9f9; 
            }
            .total-row td { 
                background: #e8f5e9 !important; 
                font-weight: bold; 
                color: #1b5e20;
            }
            .net-amount { 
                font-weight: 700; 
                color: #e91e63; 
            }

             /* Show the total row only on the last page */
            @page :last {
                .total-row {
                    display: table-row !important;
                }
            }

            /* Hide total row from all other pages except the last page */
            @media print {
                .total-row {
                    display: none !important;
                }

                /* Show only on the last page */
                @page :last {
                    .total-row {
                        display: table-row !important;
                    }
                }
            }
        }
    `,
    });

    const fetchRecords = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.post('BillDtlRpt/GetBillDtlRpt', {
                PropId: globalProperty,
                FromDate: fromDate.format('YYYY-MM-DD'),
                ToDate: toDate.format('YYYY-MM-DD'),
                ResidentId: "1",
                Flag: ""
            });
            if (response.data.Status === 0) {
                setRecords(response.data.Data || []);
                setFilteredRecords(response.data.Data || []);
                if (response.data.Data.length === 0) {
                    toast.info('No records found for the selected date range.');
                }
            } else {
                toast.info(response.data.Message || 'No records found.');
            }
        } catch (error) {
            toast.error('Error fetching records from the server.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (globalProperty) {
            fetchRecords();
        }
    }, [globalProperty]);

    useEffect(() => {
        const searchWords = searchText.trim().toLowerCase().split(/\s+/);
        const filtered = records.filter((rec) => {
            const residentWords = rec.ResidentName?.toLowerCase().split(/\s+/) || [];
            const propWords = rec.PropName?.toLowerCase().split(/\s+/) || [];
            const monthWords = rec.MonthNm?.toLowerCase().split(/\s+/) || [];
            const mobNo = rec.MobileNo?.toLowerCase().split(/\s+/) || [];
            const book = rec.BookNo?.toLowerCase().split(/\s+/) || [];
            const billDate = dayjs(rec.BillDt).format('DD-MM-YYYY').toLowerCase();
            const dueDate = dayjs(rec.PyadDt).format('DD-MM-YYYY').toLowerCase();
            const amount = rec.EffRent?.toString();
            const addon1 = rec.Addon1?.toString();
            const addon2 = rec.Addon2?.toString();
            const plnName = rec.PlanName?.toLowerCase().split(/\s+/) || [];

            return searchWords.every((word) =>
                residentWords.some((w) => w.includes(word)) ||
                propWords.some((w) => w.includes(word)) ||
                monthWords.some((w) => w.includes(word)) ||
                mobNo.some((w) => w.includes(word)) ||
                book.some((w) => w.includes(word)) ||
                billDate.includes(word) ||
                dueDate.includes(word) ||
                amount.includes(word) ||
                addon1.includes(word) ||
                addon2.includes(word) ||
                plnName.includes(word)
            );
        });

        setFilteredRecords(filtered);
    }, [searchText, records]);

    const handleLoadRecords = () => {
        fetchRecords();
    };

    const total = filteredRecords.reduce((acc, r) => acc + Number(r.EffRent || 0), 0);

    const handleExportToExcel = () => {
        if (filteredRecords.length === 0) {
            toast.info("No records to export");
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Month Wise Bill');

        // ==================== Light Blue Border Definition ====================
        const lightBlueBorder = {
            top: { style: 'thin', color: { argb: 'FF81C4FF' } },
            left: { style: 'thin', color: { argb: 'FF81C4FF' } },
            bottom: { style: 'thin', color: { argb: 'FF81C4FF' } },
            right: { style: 'thin', color: { argb: 'FF81C4FF' } }
        };

        // ==================== TITLE ====================
        worksheet.mergeCells('A1:M1');  // 13 columns
        const titleCell = worksheet.getCell('A1');
        titleCell.value = `Month Wise Bill Report | ${fromDate.format('DD-MMM-YYYY')} to ${toDate.format('DD-MMM-YYYY')}`;
        titleCell.font = { bold: true, size: 14, color: { argb: 'FFFFFFFF' } };
        titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '635BFF' } };
        titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
        titleCell.border = lightBlueBorder;

        // ==================== HEADER ROW ====================
        const headers = [
            'Property', 'Book No', 'Unit No', 'Mobile No', 'Bill Date', 'Month',
            'Resident', 'Due Date', 'PlanName', 'Addon1', 'Addon2', 'Std Amt', 'Net Amount'
        ];

        const headerRow = worksheet.addRow(headers);

        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '635BFF' } };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.border = lightBlueBorder;
        });

        headerRow.height = 22;

        // ==================== DATA ROWS ====================
        let totalNetAmount = 0;

        filteredRecords.forEach((rec, index) => {
            const netAmount = Number(rec.EffRent || 0);
            totalNetAmount += netAmount;

            const row = worksheet.addRow([
                rec.PropName || '',
                rec.BookNo || '',
                rec.BedNo || '',
                rec.MobileNo || '',
                rec.BillDt ? dayjs(rec.BillDt).format('DD-MM-YYYY') : '',
                rec.MonthNm || '',
                rec.ResidentName || '',
                rec.PyadDt ? dayjs(rec.PyadDt).format('DD-MM-YYYY') : '',
                rec.PlanName || '',
                Number(rec.Addon1 || 0),
                Number(rec.Addon2 || 0),
                Number(rec.StdRent || 0),
                netAmount
            ]);

            // Alternate row coloring + Light Blue Border
            row.eachCell((cell) => {
                if (index % 2 === 0) {
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
                }
                cell.border = lightBlueBorder;
            });

            // Amount formatting (right align + number format)
            const amountColumns = [10, 11, 12, 13]; // Addon1, Addon2, Std Amt, Net Amount (0-based index)
            amountColumns.forEach(col => {
                const cell = row.getCell(col);
                cell.numFmt = '#,##0';
                cell.alignment = { horizontal: 'right' };
            });
        });

        // ==================== TOTAL ROW ====================
        const totalRow = worksheet.addRow([
            'TOTAL', '', '', '', '', '',
            `Records: ${filteredRecords.length}`, '', '', '', '', '',
            totalNetAmount
        ]);

        totalRow.eachCell((cell, colNumber) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '635BFF' } };
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.border = lightBlueBorder;

            if (colNumber === 13) {  // Net Amount column
                cell.numFmt = '#,##0';
                cell.alignment = { horizontal: 'right' };
            }
        });

        totalRow.height = 22;

        // ==================== PROFESSIONAL SUMMARY SECTION ====================
        const summaryStartRow = worksheet.lastRow.number + 2;

        const summaryData = [
            { label: "Total Records", value: filteredRecords.length },
            { label: "Total Net Amount", value: totalNetAmount }
        ];

        summaryData.forEach((item, idx) => {
            const labelCell = worksheet.getCell(`K${summaryStartRow + idx}`);
            const valueCell = worksheet.getCell(`L${summaryStartRow + idx}`);

            labelCell.value = item.label;
            valueCell.value = item.value;

            labelCell.font = { bold: true };
            valueCell.font = { bold: true };

            valueCell.numFmt = '#,##0';
            valueCell.alignment = { horizontal: 'right' };

            // Light blue border for summary
            labelCell.border = lightBlueBorder;
            valueCell.border = lightBlueBorder;

            // Highlight total amount
            if (idx === 1) {
                valueCell.font = { bold: true, color: { argb: '006400' } }; // Dark green for total
            }
        });

        // ==================== COLUMN WIDTHS ====================
        worksheet.columns = [
            { width: 15 },  // A Property
            { width: 12 },  // B Book No
            { width: 12 },  // C Unit No
            { width: 14 },  // D Mobile No
            { width: 13 },  // E Bill Date
            { width: 12 },  // F Month
            { width: 24 },  // G Resident
            { width: 13 },  // H Due Date
            { width: 20 },  // I PlanName
            { width: 12 },  // J Addon1
            { width: 12 },  // K Addon2
            { width: 14 },  // L Std Amt
            { width: 16 }   // M Net Amount
        ];

        // ==================== EXPORT ====================
        workbook.xlsx.writeBuffer().then((buffer) => {
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const fileName = `MonthWiseBill_${fromDate.format('DDMMYYYY')}_to_${toDate.format('DDMMYYYY')}.xlsx`;
            saveAs(blob, fileName);
        });
    };

    const handleIconClick = (rowData) => {
        handleBillingDetails(rowData);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    const getRoomPlanId = (rowData) => {
        return (
            rowData.RoomPlanID ||
            rowData.roomPlanId ||
            rowData.RoomPlanId ||
            rowData.roomplanid ||
            null
        );
    };

    const handleBillingDetails = async (rowData) => {
        if (!rowData.BookId) {
            toast.info("No transaction is visible");
            return;
        }

        const roomPlanId = getRoomPlanId(rowData);

        try {
            setIsLoading(true);
            const response = await axiosInstance.post('TrnBooking/GetReportTrnBooking', {
                BookId: rowData.BookId,
                Flag: "R"
            });

            if (response.data.Status === 0 && response.data.Data) {
                setBillingDetails(response.data.Data);
                setOpenModal(true);

                if (roomPlanId) {
                    await fetchPlanDataState(roomPlanId);
                } else {
                    toast.warn("RoomPlanId details not available");
                }

                await fetchPayAdvice(rowData.BookId);
                await fetchPayGenerate(rowData.BookId);
                await fetchPendingDetails(rowData.BookId);
                await fetchSummaryReport(rowData.BookId);
                await fetchReceiptReport(rowData.BookId);
            } else {
                toast.error('No billing details found');
            }
        } catch (error) {
            toast.error('Error fetching billing details');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchPlanDataState = async (PlanId) => {
        if (!PlanId || PlanId === "null" || PlanId === null) {
            console.warn("Invalid RoomPlanId:", PlanId);
            return;
        }

        try {
            const response = await axiosInstance.post(`MstRoomPlan/RetriveMstRoomPlan`, {
                RoomPlanID: PlanId,
                Flag: "R"
            });

            if (response.data.Status === 0) {
                setPlanDataState(response.data.Data);
            }
        } catch (error) {
            toast.error('Error fetching plan data:', error);
        }
    };

    const fetchPayAdvice = async (BookId) => {
        try {
            const currentDate = new Date().toISOString().split('T')[0];
            const response = await axiosInstance.post(`TrnBooking/GetSelectTrnPytAdv`, {
                PytAdvDt: currentDate,
                Flag: "BookingWise",
                BookId: BookId
            });

            if (response.data.Status === 0) {
                setPayAdvice(response.data.Data);
            }
        } catch (error) {
            toast.error('Error fetching payment advice:', error);
        }
    };

    const fetchPayGenerate = async (BookId) => {
        try {
            const currentDate = new Date().toISOString().split('T')[0];
            const response = await axiosInstance.post(`TrnBooking/GetSelectTrnPytAdv`, {
                PytAdvDt: currentDate,
                Flag: "GenPytAdv",
                BookId: BookId
            });

            if (response.data.Status === 0) {
                setPayGenerate(response.data.Data);
            }
        } catch (error) {
            toast.error('Error fetching pay generate data:', error);
        }
    };

    const fetchSummaryReport = async (BookId) => {
        try {
            const currentDate = new Date().toISOString().split('T')[0];
            const response = await axiosInstance.post(`TrnBooking/GetSelectTrnPytAdv`, {
                PytAdvDt: currentDate,
                Flag: "GenAccSum",
                BookId: BookId
            });

            if (response.data.Status === 0) {
                setSummaryReport(response.data.Data);
            }
        } catch (error) {
            toast.error('Error fetching summary report:', error);
        }
    };

    const fetchReceiptReport = async (BookId) => {
        try {
            const currentDate = new Date().toISOString().split('T')[0];
            const response = await axiosInstance.post('BookingReports/GetBookingReportsDetail', {
                BookDtFrom: currentDate,
                BookUpToDt: currentDate,
                PropId: "0",
                Flag: "PyAdvWiseRecVchr",
                Id: BookId
            });

            if (response.data.Status === 0) {
                setReceiptSummary(response.data.Data);
            }
        } catch (error) {
            toast.error('Error fetching receipt report:', error);
        }
    };

    const fetchPendingDetails = async (BookId) => {
        try {
            const currentDate = new Date().toISOString().split('T')[0];
            const response = await axiosInstance.post(`TrnBooking/GetSelectTrnPytAdv`, {
                Flag: "UnBilledAdvPyt",
                BookId: BookId,
                PytAdvDt: currentDate
            });

            if (response.data.Status === 0) {
                setPendingDetails(response.data.Data);
            }
        } catch (error) {
            toast.error('Error fetching pending details:', error);
        }
    };

    return (
        <Box
            sx={{
                width: '100%',
                height: '90vh',
                padding: '10px',
                backgroundColor: 'rgb(249, 249, 249)',
                position: 'relative'
            }}
        >
            <Box
                sx={{
                    backgroundColor: "#ffffff",
                    padding: { xs: "8px 16px", sm: "12px 16px" },
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                    borderRadius: "12px",
                    margin: "10px auto",
                    maxWidth: "95%",
                    flexDirection: { xs: "column", sm: "column", md: "row" },
                    gap: 2,
                }}
            >
                <Breadcrumbs
                    aria-label="breadcrumb"
                    separator="›"
                    sx={{
                        fontSize: { xs: "14px", sm: "16px" },
                        display: "flex",
                        flexDirection: { xs: "column", sm: "column", md: "row" }
                    }}
                >
                    <Link
                        underline="hover"
                        color="#635bff"
                        sx={{ cursor: "pointer", fontWeight: "500", display: "flex", alignItems: "center" }}
                        onClick={() => navigate('/dashboard')}
                    >
                        <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                        <Box component="span">Home</Box>
                    </Link>
                    <Link
                        underline="hover"
                        color="#635bff"
                        sx={{ cursor: "pointer", fontWeight: "500" }}
                        onClick={() => navigate('/reports/accountsFinance')}
                    >
                        Account & Finance
                    </Link>

                    <Typography color="text.primary" sx={{ fontWeight: "500" }}>
                        Month Wise Bill
                    </Typography>
                </Breadcrumbs>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1}
                        sx={{
                            flexWrap: "nowrap",
                            alignItems: "center",
                            justifyContent: { xs: "flex-start", md: "flex-end" },
                            width: "auto",
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                width: { xs: '100%', md: 'auto' },
                                justifyContent: { xs: 'space-between', md: 'flex-start' },
                                flexWrap: { xs: 'nowrap', md: 'nowrap' },
                            }}
                        >
                            <DatePicker
                                label="From Date"
                                value={fromDate}
                                onChange={(newValue) => setFromDate(newValue)}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        sx: {
                                            flex: { xs: '0 1 48%', md: '0 1 160px' },
                                            bgcolor: 'white',
                                            borderRadius: 2,
                                            '& .MuiInputBase-root': { fontSize: '1rem', py: 0.2 },
                                            '& .MuiOutlinedInput-input': { padding: '4px 8px' },
                                        },
                                    },
                                }}
                                maxDate={toDate}
                                format="DD/MM/YYYY"
                            />
                            <DatePicker
                                label="To Date"
                                value={toDate}
                                onChange={(newValue) => setToDate(newValue)}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        sx: {
                                            flex: { xs: '0 1 48%', md: '0 1 160px' },
                                            bgcolor: 'white',
                                            borderRadius: 2,
                                            '& .MuiInputBase-root': { fontSize: '1rem', py: 0.2 },
                                            '& .MuiOutlinedInput-input': { padding: '4px 8px' },
                                        },
                                    },
                                }}
                                minDate={fromDate}
                                format="DD/MM/YYYY"
                            />
                        </Stack>
                        <Stack direction="row" spacing={1}
                            sx={{
                                mt: { xs: 1, md: 0 },
                                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                                width: { xs: '100%', md: 'auto' },
                            }}
                        >
                            <Tooltip title='Retrieve data' arrow>
                                <span>
                                    <Button
                                        variant="contained"
                                        onClick={handleLoadRecords}
                                        disabled={!fromDate || !toDate}
                                        sx={{
                                            height: 30,
                                            bgcolor: '#635bff',
                                            color: 'white',
                                            fontSize: '0.8rem',
                                            fontWeight: 'medium',
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            px: 2,
                                            '&:hover': {
                                                bgcolor: '#635bff',
                                            },
                                        }}
                                    >
                                        Get Data
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title='Print bill records' arrow>
                                <span>
                                    <Button
                                        variant="outlined"
                                        startIcon={<PrintIcon />}
                                        onClick={handlePrint}
                                        disabled={records.length === 0}
                                        sx={{
                                            height: 30,
                                            borderColor: 'secondary.main',
                                            color: 'secondary.main',
                                            fontSize: '0.8rem',
                                            fontWeight: 'medium',
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            px: 2,
                                            '&:hover': {
                                                borderColor: 'secondary.dark',
                                                boxShadow: theme.shadows[4],
                                                transform: 'translateY(-1px)',
                                                transition: 'all 0.2s ease-in-out',
                                            },
                                        }}
                                    >
                                        Print
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title='Export to excel' arrow>
                                <span>
                                    <Button
                                        variant="outlined"
                                        startIcon={<ImportExportIcon />}
                                        onClick={handleExportToExcel}
                                        disabled={records.length === 0}
                                        sx={{
                                            height: 30,
                                            borderColor: 'success.main',
                                            color: 'success.main',
                                            fontSize: '0.8rem',
                                            fontWeight: 'medium',
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            px: 2,
                                            '&:hover': {
                                                borderColor: 'success.dark',
                                                boxShadow: theme.shadows[4],
                                                transform: 'translateY(-1px)',
                                                transition: 'all 0.2s ease-in-out',
                                            },
                                        }}
                                    >
                                        Export
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title='Previous Page' arrow>
                                <IconButton sx={{ width: 32, height: 32, color: '#ffffff', bgcolor: '#635bff', ":hover": { backgroundColor: '#635bff' } }}
                                    onClick={() => navigate('/reports/accountsFinance')}
                                >
                                    <ArrowForwardIcon />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Stack>
                </LocalizationProvider>
            </Box>

            <Container
                maxWidth={false}
                sx={{
                    width: '95%', mt: 2, p: 3, pb: 1, bgcolor: 'grey.50', borderRadius: 4,
                    boxShadow: theme.shadows[10],
                    mx: 'auto',
                }}
            >
                <ToastContainer />
                <Box sx={{ displayPrint: 'none' }}>
                    {/* <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            justifyContent: 'space-between',
                            alignItems: { xs: 'flex-start', md: 'center' },
                            mb: 2,
                        }}
                    >
                        <Zoom in timeout={1000}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: { xs: 2, md: 0 },
                                    flexShrink: 0,
                                    minWidth: 0,
                                }}
                            >
                                <CalendarMonthIcon sx={{ fontSize: 30, color: 'primary.main', mr: 1 }} />
                                <Typography variant="h5" noWrap
                                    sx={{
                                        fontWeight: 'bold',
                                        color: 'primary.dark',
                                        letterSpacing: 1,
                                        textTransform: 'none',
                                        background: `linear-gradient(90deg, ${theme.palette.primary.main},${theme.palette.secondary.main}, ${theme.palette.warning.main})`,
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        userSelect: 'none'
                                    }}
                                >
                                    Month Wise Bill
                                </Typography>
                            </Box>
                        </Zoom>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={1}
                                sx={{
                                    justifyContent: { xs: 'flex-start', md: 'flex-end' },
                                    alignItems: { xs: 'stretch', md: 'center' },
                                    width: '100%',
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{
                                        width: { xs: '100%', md: 'auto' },
                                        justifyContent: { xs: 'space-between', md: 'flex-start' },
                                        flexWrap: { xs: 'nowrap', md: 'nowrap' },
                                    }}
                                >
                                    <DatePicker
                                        label="From Date"
                                        value={fromDate}
                                        onChange={(newValue) => setFromDate(newValue)}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                fullWidth: true,
                                                sx: {
                                                    minWidth: { xs: '48%', md: 160 },
                                                    bgcolor: 'white',
                                                    borderRadius: 2,
                                                    '& .MuiInputBase-root': { fontSize: '1rem', py: 0.2 },
                                                    '& .MuiOutlinedInput-input': {
                                                        padding: '4px 8px',
                                                    },
                                                },
                                            },
                                        }}
                                        maxDate={toDate}
                                        format="DD/MM/YYYY"
                                    />
                                    <DatePicker
                                        label="To Date"
                                        value={toDate}
                                        onChange={(newValue) => setToDate(newValue)}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                fullWidth: true,
                                                sx: {
                                                    minWidth: { xs: '48%', md: 160 },
                                                    bgcolor: 'white',
                                                    borderRadius: 2,
                                                    '& .MuiInputBase-root': { fontSize: '1rem', py: 0.2 },
                                                    '& .MuiOutlinedInput-input': {
                                                        padding: '4px 8px',
                                                    },
                                                },
                                            },
                                        }}
                                        minDate={fromDate}
                                        format="DD/MM/YYYY"
                                    />
                                </Stack>
                                <Stack direction="row" spacing={1}
                                    sx={{
                                        mt: { xs: 1, md: 0 },
                                        justifyContent: { xs: 'flex-start', md: 'flex-end' },
                                        width: { xs: '100%', md: 'auto' },
                                    }}
                                >
                                    <Tooltip title='Retrieve data' arrow>
                                        <span>
                                            <Button
                                                variant="contained"
                                                onClick={handleLoadRecords}
                                                disabled={!fromDate || !toDate}
                                                sx={{
                                                    height: 30,
                                                    bgcolor: 'primary.main',
                                                    color: 'white',
                                                    fontSize: '0.8rem',
                                                    fontWeight: 'medium',
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                    px: 2,
                                                    '&:hover': {
                                                        bgcolor: 'primary.dark',
                                                        boxShadow: theme.shadows[4],
                                                        transform: 'translateY(-1px)',
                                                        transition: 'all 0.2s ease-in-out',
                                                    },
                                                }}
                                            >
                                                Get Data
                                            </Button>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title='Print bill records' arrow>
                                        <span>
                                            <Button
                                                variant="outlined"
                                                startIcon={<PrintIcon />}
                                                onClick={handlePrint}
                                                disabled={records.length === 0}
                                                sx={{
                                                    height: 30,
                                                    borderColor: 'secondary.main',
                                                    color: 'secondary.main',
                                                    fontSize: '0.8rem',
                                                    fontWeight: 'medium',
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                    px: 2,
                                                    '&:hover': {
                                                        borderColor: 'secondary.dark',
                                                        boxShadow: theme.shadows[4],
                                                        transform: 'translateY(-1px)',
                                                        transition: 'all 0.2s ease-in-out',
                                                    },
                                                }}
                                            >
                                                Print
                                            </Button>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title='Export to excel' arrow>
                                        <span>
                                            <Button
                                                variant="outlined"
                                                startIcon={<ImportExportIcon />}
                                                onClick={handleExportToExcel}
                                                disabled={records.length === 0}
                                                sx={{
                                                    height: 30,
                                                    borderColor: 'success.main',
                                                    color: 'success.main',
                                                    fontSize: '0.8rem',
                                                    fontWeight: 'medium',
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                    px: 2,
                                                    '&:hover': {
                                                        borderColor: 'success.dark',
                                                        boxShadow: theme.shadows[4],
                                                        transform: 'translateY(-1px)',
                                                        transition: 'all 0.2s ease-in-out',
                                                    },
                                                }}
                                            >
                                                Export
                                            </Button>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title='Previous Page' arrow>
                                        <IconButton sx={{ width: 32, height: 32, color: '#ffffff', bgcolor: '#635bff', ":hover": { backgroundColor: '#635bff' } }}
                                            onClick={() => navigate('/reports/accountsFinance')}
                                        >
                                            <ArrowForwardIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Stack>
                        </LocalizationProvider>
                    </Box> */}

                    {/* Search Bar */}
                    <Box sx={{ mb: 1 }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search by Resident, Mobile No, Booking No, Amount, etc."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: 'primary.main', fontSize: '1.2rem' }} />
                                    </InputAdornment>
                                ),
                                sx: {
                                    bgcolor: 'white',
                                    borderRadius: 2,
                                    '& .MuiInputBase-input': { fontSize: '0.85rem', py: 0.8 },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'primary.main',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'primary.dark',
                                        boxShadow: theme.shadows[2],
                                    },
                                },
                            }}
                            sx={{ maxWidth: '100vw', mx: 'auto' }}
                        />
                    </Box>

                    <Divider sx={{ mb: 1, borderColor: 'grey.200', borderWidth: 1 }} />

                    {loading ? (
                        <Box sx={{ width: '100%', mb: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                            <CircularProgress size="3rem" sx={{ mb: 1 }} />
                            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                Loading data...
                            </Typography>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                maxHeight: '65vh',
                                overflowY: 'auto',
                                overflowX: 'auto',
                                position: 'relative',
                                '@media print': {
                                    maxHeight: 'none',
                                    overflow: 'visible',
                                },
                            }}
                            ref={contentRef}
                        >
                            {filteredRecords.length === 0 ? (
                                <Fade in timeout={700}>
                                    <Typography
                                        sx={{
                                            textAlign: 'center',
                                            color: 'text.secondary',
                                            py: 4,
                                            fontSize: '0.9rem',
                                            fontWeight: 'medium',
                                            fontStyle: 'italic',
                                        }}
                                    >
                                        No Bill found for the selected date range or search criteria. Please adjust and try again.
                                    </Typography>
                                </Fade>
                            ) : (
                                <Fade in timeout={700}>
                                    <Table sx={{ width: '100%' }}>
                                        <TableHead>
                                            <TableRow>
                                                {[
                                                    'Acc', 'Property', 'Book No', 'Unit No', 'Mobile No', 'Bill Date', 'Month', 'Resident',
                                                    'PaymentPlan', 'AddOn1', 'AddOn2', 'StdRent', 'NetAmt', 'Due Date',
                                                ].map((header, idx) => (
                                                    <TableCell
                                                        key={idx}
                                                        sx={{
                                                            background: '#635bff',
                                                            color: '#fff',
                                                            fontWeight: 700,
                                                            fontSize: '0.85rem',
                                                            position: 'sticky',
                                                            padding: '4px',
                                                            top: 0,
                                                            zIndex: 11,
                                                            boxShadow: '0 3px 6px rgba(0,0,0,0.15)',
                                                            textAlign: 'left',
                                                            textTransform: 'none',
                                                            letterSpacing: '0.5px',
                                                        }}
                                                    >
                                                        {header}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {filteredRecords.map((rec, idx) => (
                                                <TableRow
                                                    key={rec.id || idx}
                                                    sx={{
                                                        backgroundColor: idx % 2 === 0 ? '#f9f9f9ff' : '#fff',
                                                        transition: 'background 0.2s',
                                                        '&:hover': {
                                                            backgroundColor: '#e3f2fd',
                                                        },
                                                    }}
                                                >
                                                    <TableCell sx={cellStyle}>
                                                        <Tooltip title="View Account Details" arrow>
                                                            <AccountCircleIcon
                                                                sx={{
                                                                    cursor: rec.BookId ? 'pointer' : 'not-allowed',
                                                                    color: rec.BookId ? '#635bff' : '#aaa',
                                                                    fontSize: '1.2rem'
                                                                }}
                                                                onClick={() => rec.BookId && handleBillingDetails(rec)}
                                                            />
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell sx={cellStyle}>{rec.PropName}</TableCell>
                                                    <TableCell sx={cellStyle}>{rec.BookNo}</TableCell>
                                                    <TableCell sx={cellStyle}>{rec.BedNo}</TableCell>
                                                    <TableCell sx={cellStyle}>{rec.MobileNo}</TableCell>
                                                    <TableCell sx={cellStyle}>{dayjs(rec.BillDt).format('DD-MM-YYYY')}</TableCell>
                                                    <TableCell sx={cellStyle}>{rec.MonthNm}</TableCell>
                                                    <TableCell sx={cellStyle}>{rec.ResidentName}</TableCell>
                                                    <TableCell sx={cellStyle}>{rec.PlanName}</TableCell>
                                                    <TableCell sx={cellStyle}>{rec.Addon1}</TableCell>
                                                    <TableCell sx={cellStyle}>{rec.Addon2}</TableCell>
                                                    <TableCell sx={cellStyle}>{rec.StdRent}</TableCell>
                                                    <TableCell sx={{ ...cellStyle, fontWeight: 700, color: theme.palette.secondary.main }}>
                                                        ₹{Number(rec.EffRent).toLocaleString('en-IN')}
                                                    </TableCell>
                                                    <TableCell sx={cellStyle}>{dayjs(rec.PyadDt).format('DD-MM-YYYY')}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>

                                        <TableFooter>
                                            <TableRow className="total-row">
                                                <TotalCell colSpan={12}>
                                                    Total Records: {filteredRecords.length}
                                                </TotalCell>
                                                <TotalCell
                                                    colSpan={12}
                                                    sx={{
                                                        fontWeight: 800,
                                                        textAlign: 'right',
                                                        color: '#1b5e20',
                                                    }}
                                                >
                                                    Total: ₹{total.toLocaleString('en-IN')}
                                                </TotalCell>
                                            </TableRow>
                                        </TableFooter>
                                    </Table>
                                </Fade>
                            )}
                        </Box>
                    )}
                </Box>

                <BillingDetailsModal
                    open={openModal}
                    onClose={handleCloseModal}
                    billingData={billingDetails}
                    planData={planDataState}
                    payAdvice={payAdvice}
                    payGenerate={payGenerate}
                    pendingDetails={pendingDetails}
                    summaryReport={summaryReport}
                    receiptSummary={receiptSummary}
                />
            </Container>
        </Box>
    );
};

// Compact cell style – reduced height
const cellStyle = {
    padding: '3px 6px',
    fontSize: '0.82rem',
    borderBottom: '1px solid #e0e0e0',
    whiteSpace: 'nowrap',
};

const TotalCell = styled(TableCell)(({ theme }) => ({
    ...cellStyle,
    fontWeight: 700,
    backgroundColor: '#e8f5e9',
    color: '#000',
    position: 'sticky',
    bottom: 0,
    zIndex: 10,
    boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
}));

export default MonthWiseBill;